import { Injectable, OnDestroy } from '@angular/core';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { FundDataServiceParams } from '@products/services/graphql-fund-data.service';
import usTaxData from '@graphql/tax/us-tax.graphql';
import { Logger } from '@utils/logger';
import { Product } from '@models';
import { Subject, forkJoin } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TaxState } from '@products/types/tax.type';
import { TranslateService } from '@shared/translate/translate.service';
import { UsTaxDistribution } from '@products/models/us-tax';
import { DynamicTableColDef } from '@frk/eds-components';
import { ExcelDataType, WorkBook, WorkSheet, XLSX$Utils } from 'xlsx';
import { EMDASH } from '@products/utils/constants/product.constants';
import {
  EMPTY_ROW,
  ExcelDownloadService,
  FtCellObject,
  getCellObject,
  getColWidths,
} from '@products/services/excel-download.service';

const logger = Logger.getLogger('UsTaxService');
@Injectable({
  providedIn: 'root',
})
export class UsTaxService implements OnDestroy {
  state: TaxState = {};
  taxState$ = new Subject<TaxState>();
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private fundOverviewService: FundOverviewService,
    private translateService: TranslateService,
    private excelService: ExcelDownloadService
  ) {}

  /**
   * Fetches data from us tax pds service
   * @param fundDataServiceParams fund params
   * @param initializeFlag boolean value
   */
  populate(fundDataServiceParams: FundDataServiceParams, initializeFlag) {
    this.fundOverviewService
      .register(usTaxData, fundDataServiceParams)
      .pipe(take(1))
      .subscribe((data: Product) => {
        logger.debug(data);
        this.state.federalTaxId = data.shareClasses[0].federalTaxId;
        const tax = data.shareClasses[0].tax;
        this.state.taxData = tax;
        if (tax?.states?.length <= 10) {
          this.state.stateCols = 1;
        } else if (tax?.states?.length <= 20) {
          this.state.stateCols = 2;
        } else {
          this.state.stateCols = 3;
        }
        if (fundDataServiceParams.year === this.getLastYear()) {
          this.state.noDataMessage = !tax?.distributions?.length
            ? (this.state.noDataMessage = this.translateService
                .instant(
                  'products.us-tax-no-data-message',
                  fundDataServiceParams.fundId
                )
                .replace('{{year}}', String(fundDataServiceParams.year)))
            : null;
        } else {
          this.state.noDataMessage = !tax?.distributions?.length
            ? (this.state.noDataMessage = this.translateService
                .instant('products.us-tax-no-historic-data-message')
                .replace('{{year}}', String(fundDataServiceParams.year)))
            : null;
        }
        this.state.currentYear = fundDataServiceParams.year;
        this.state.fundName = data.fundName;
        this.state.pdf =
          data.investmentManager !== 'Franklin Templeton' &&
          this.state.currentYear < 2021
            ? this.translateService.instant(
                `products.us-tax-lm-pdf-download-${this.state.currentYear}`
              )
            : null;
        if (initializeFlag) {
          this.state.years = this.calculateYears(
            data.shareClasses[0].inceptionDateStd
          );
        }
        this.taxState$.next(this.state);
      });
  }

  /**
   * Calculates the year values for dropdown
   * @param shareClassInceptionDate represents inception date
   * @returns array of years
   */
  calculateYears(shareClassInceptionDate: string): number[] {
    const shareClassInceptionYear = new Date(
      shareClassInceptionDate
    ).getFullYear();
    const firstYear = Math.max(2008, shareClassInceptionYear);
    const lastYear = this.getLastYear();
    const numberOfYears = 1 + (lastYear - firstYear);
    return Array(numberOfYears)
      .fill(null)
      .map((val, i) => lastYear - i);
  }

  getLastYear(): number {
    return new Date().getFullYear() - 1;
  }

  /**
   * Defines header objects
   */
  private getColDefs(): DynamicTableColDef<UsTaxDistribution>[] {
    return [
      {
        field: 'recordDate',
        headerName: this.translateService.instant(
          'products.us-tax-record-date'
        ),
        dataType: 'date',
      },
      {
        field: 'ordinaryDividends',
        headerName: this.translateService.instant(
          'products.us-tax-ordinary-dividends'
        ),
      },
      {
        field: 'qualifiedDividends',
        headerName: this.translateService.instant(
          'products.us-tax-qualified-dividends'
        ),
      },
      {
        field: 'capGainDistributions',
        headerName: this.translateService.instant(
          'products.us-tax-cap-gain-distributions'
        ),
      },
      {
        field: 'unrecapturedSection1250Gain',
        headerName: this.translateService.instant(
          'products.us-tax-unrecaptured-section-1250-gain'
        ),
      },
      {
        field: 'nonTaxableReturnOfCapital',
        headerName: this.translateService.instant(
          'products.us-tax-nontaxable-return-of-capital'
        ),
      },
      {
        field: 'section199aDividends',
        headerName: this.translateService.instant(
          'products.us-tax-section-199a-dividends'
        ),
      },
      {
        field: 'foriegnTaxPaid',
        headerName: this.translateService.instant(
          'products.us-tax-foreign-tax-paid'
        ),
      },
      {
        field: 'exemptInterestDividends',
        headerName: this.translateService.instant(
          'products.us-tax-exempt-interest-dividends'
        ),
      },
      {
        field: 'interestTaxReclaimRefund',
        headerName: this.translateService.instant(
          'products.interest-on-tax-reclaim-refunds'
        ),
      },
    ];
  }
  /**
   * Fills the excel with '-' if empty
   * @param taxDistribution table data
   * @returns excel cell valu for headers
   */
  private getEmptyCellValue = (taxDistribution: UsTaxDistribution) => (
    colDef: DynamicTableColDef<UsTaxDistribution>
  ): FtCellObject => {
    const xlsCellValue = taxDistribution[colDef.field]
      ? taxDistribution[colDef.field]
      : EMDASH;
    let dataType: ExcelDataType;
    switch (colDef.dataType) {
      case 'date':
        dataType = 'd';
        break;
      default:
        dataType = 's';
        break;
    }
    return getCellObject(xlsCellValue, dataType);
  };

  /**
   * gets excel cell object for header
   * @param colDef includes objects for excel header columns
   * @returns excel cell object
   */
  private getHeader = (
    colDef: DynamicTableColDef<UsTaxDistribution>
  ): FtCellObject => {
    return getCellObject(colDef.headerName, 's');
  };

  /**
   * Creates a worksheet from inputs and downloads excel
   * @param topCaveat caveat displayed at the top of excel
   * @param bottomCaveat caveat displayed at the top of excel
   * @param xlsxHeaders table headers displayed in excel
   * @param rows table data displayed in excel
   */
  private createXlsx(
    topCaveat: FtCellObject[][],
    bottomCaveat: FtCellObject[][],
    xlsxHeaders: FtCellObject[][],
    rows: FtCellObject[][]
  ): void {
    const fundNameRow: FtCellObject[][] = [
      [getCellObject(this.state?.fundName, 's')],
    ];
    const federalTaxId: FtCellObject[][] = [
      [
        getCellObject(
          this.translateService.instant('products.us-tax-federal-id-number'),
          's'
        ),
        getCellObject(this.state?.federalTaxId?.toString(), 's'),
      ],
    ];
    const xlsxTaxYear: FtCellObject[][] = [
      [
        getCellObject(this.translateService.instant('products.year'), 's'),
        getCellObject(this.state?.currentYear?.toString(), 'n'),
      ],
    ];
    const dataCells: FtCellObject[][] = fundNameRow
      .concat(EMPTY_ROW)
      .concat(xlsxTaxYear)
      .concat(federalTaxId)
      .concat(EMPTY_ROW)
      .concat(xlsxHeaders)
      .concat(rows);

    // assemble into 2D array of cells and return
    const sheetCells: FtCellObject[][] = topCaveat
      .concat(EMPTY_ROW)
      .concat(dataCells)
      .concat(EMPTY_ROW, EMPTY_ROW, EMPTY_ROW, EMPTY_ROW, EMPTY_ROW)
      .concat(bottomCaveat);

    import('xlsx').then((xlsx) => {
      const xlsxUtils: XLSX$Utils = xlsx.utils;

      const workSheet: WorkSheet = xlsxUtils.aoa_to_sheet(sheetCells);
      workSheet['!cols'] = getColWidths(dataCells);
      const workbook: WorkBook = xlsx.utils.book_new();
      xlsxUtils.book_append_sheet(
        workbook,
        workSheet,
        this.translateService.instant(
          'products.us-distributions-tax-info-tab-name'
        )
      );
      xlsx.writeFile(
        workbook,
        this.translateService.instant(
          'products.us-distributions-tax-info-file-name'
        ) + '.xlsx'
      );
    });
  }

  /**
   * Creates table headers and table rows from distributions date
   */
  public downloadTaxInfoExcel(): void {
    const colDefs: DynamicTableColDef<UsTaxDistribution>[] = this.getColDefs();
    const xlsHeaders: FtCellObject[][] = [colDefs.map(this.getHeader)];
    const rowData: FtCellObject[][] = this.state?.taxData?.distributions.map(
      (taxDistribution: UsTaxDistribution) => {
        // Adding a condition to add total label to excel
        taxDistribution.recordDate =
          taxDistribution.recordType ===
          this.translateService.instant('products.tax-info-record-type')
            ? taxDistribution.recordDate
            : this.translateService.instant('products.us-tax-total');

        return colDefs.map(this.getEmptyCellValue(taxDistribution));
      }
    );
    // Load Caveats
    forkJoin([
      this.excelService.getProximalCaveat$('UsTaxDistributionsExcelTop'),
      this.excelService.getProximalCaveat$('UsTaxDistributionsExcelBottom'),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([topCaveat, bottomCaveat]) => {
        this.createXlsx(topCaveat, bottomCaveat, xlsHeaders, rowData);
      });
  }

  /**
   * Called while component is destroyed
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
